// MCHD Room Scheduler - Block Statistics Component
// Displays comprehensive statistics for time blocks

import React from 'react';
import {
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Group,
  Progress,
  ThemeIcon,
  Alert,
  Center,
  Loader,
  Card,
  Badge,
} from '@mantine/core';
import {
  IconBlockquote,
  IconCheck,
  IconX,
  IconCalendar,
  IconClock,
  IconAlertCircle,
} from '@tabler/icons-react';

// Import types
import type { BlockStats as BlockStatsType } from '../../api/endpoints/blocks';

// =============================================================================
// INTERFACES
// =============================================================================

interface BlockStatsProps {
  stats?: BlockStatsType;
  isLoading?: boolean;
  error?: any;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Block Statistics Component
 * Displays comprehensive statistics and metrics for time blocks
 */
export function BlockStats({ stats, isLoading = false, error }: BlockStatsProps): JSX.Element {
  // =============================================================================
  // RENDER
  // =============================================================================

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Error Loading Statistics"
        color="red"
      >
        {error.message || 'An error occurred while loading block statistics'}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Paper p="lg" withBorder>
        <Center p="xl">
          <Stack align="center" gap="md">
            <Loader size="md" />
            <Text c="dimmed">Loading block statistics...</Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  if (!stats) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="No Data Available"
        color="yellow"
      >
        Block statistics are not available at this time.
      </Alert>
    );
  }

  const {
    totalBlocks = 0,
    activeBlocks = 0,
    inactiveBlocks = 0,
    blocksByMonth = [],
    upcomingBlocks = 0,
    expiredBlocks = 0,
  } = stats || {};

  const activePercentage = totalBlocks > 0 ? (activeBlocks / totalBlocks) * 100 : 0;
  const upcomingPercentage = totalBlocks > 0 ? (upcomingBlocks / totalBlocks) * 100 : 0;
  const expiredPercentage = totalBlocks > 0 ? (expiredBlocks / totalBlocks) * 100 : 0;

  return (
    <Stack gap="md">
      {/* Main Statistics Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <StatCard
          title="Total Blocks"
          value={totalBlocks}
          icon={<IconBlockquote size={20} />}
          color="blue"
          description="All time blocks"
        />
        <StatCard
          title="Active Blocks"
          value={activeBlocks}
          icon={<IconCheck size={20} />}
          color="green"
          description={`${activePercentage.toFixed(1)}% of total`}
        />
        <StatCard
          title="Upcoming Blocks"
          value={upcomingBlocks}
          icon={<IconCalendar size={20} />}
          color="orange"
          description={`${upcomingPercentage.toFixed(1)}% of total`}
        />
        <StatCard
          title="Expired Blocks"
          value={expiredBlocks}
          icon={<IconClock size={20} />}
          color="red"
          description={`${expiredPercentage.toFixed(1)}% of total`}
        />
      </SimpleGrid>

      {/* Status Distribution */}
      <Paper p="md" withBorder>
        <Text fw={500} mb="md">Block Status Distribution</Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="sm" color="green" variant="light">
                <IconCheck size={12} />
              </ThemeIcon>
              <Text size="sm">Active Blocks</Text>
            </Group>
            <Group gap="xs">
              <Text size="sm" fw={500}>{activeBlocks}</Text>
              <Badge color="green" size="sm">{activePercentage.toFixed(1)}%</Badge>
            </Group>
          </Group>
          <Progress value={activePercentage} color="green" size="sm" />
          
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="sm" color="red" variant="light">
                <IconX size={12} />
              </ThemeIcon>
              <Text size="sm">Inactive Blocks</Text>
            </Group>
            <Group gap="xs">
              <Text size="sm" fw={500}>{inactiveBlocks}</Text>
              <Badge color="red" size="sm">{(100 - activePercentage).toFixed(1)}%</Badge>
            </Group>
          </Group>
          <Progress value={100 - activePercentage} color="red" size="sm" />
        </Stack>
      </Paper>

      {/* Monthly Distribution */}
      {blocksByMonth.length > 0 && (
        <Paper p="md" withBorder>
          <Text fw={500} mb="md">Blocks by Month</Text>
          <Stack gap="sm">
            {blocksByMonth.slice(0, 6).map((monthData) => (
              <div key={monthData.month}>
                <Group justify="space-between" mb="xs">
                  <Text size="sm">{monthData.month}</Text>
                  <Badge color="blue" size="sm">{monthData.count}</Badge>
                </Group>
                <Progress 
                  value={(monthData.count / Math.max(...blocksByMonth.map(m => m.count))) * 100} 
                  color="blue" 
                  size="sm" 
                />
              </div>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Quick Insights */}
      <Paper p="md" withBorder>
        <Text fw={500} mb="md">Quick Insights</Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text size="sm">Total Active Rate</Text>
            <Badge color={activePercentage >= 70 ? 'green' : activePercentage >= 40 ? 'yellow' : 'red'} size="sm">
              {activePercentage.toFixed(1)}%
            </Badge>
          </Group>
          <Group justify="space-between">
            <Text size="sm">Upcoming Blocks</Text>
            <Badge color={upcomingBlocks > 0 ? 'blue' : 'gray'} size="sm">
              {upcomingBlocks} scheduled
            </Badge>
          </Group>
          <Group justify="space-between">
            <Text size="sm">Expired Blocks</Text>
            <Badge color={expiredBlocks > 0 ? 'orange' : 'green'} size="sm">
              {expiredBlocks} completed
            </Badge>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Stat Card Component
 * Displays a single statistic with icon and description
 */
function StatCard({ 
  title, 
  value, 
  icon, 
  color, 
  description 
}: { 
  title: string; 
  value: number; 
  icon: React.ReactNode; 
  color: string; 
  description: string; 
}): JSX.Element {
  return (
    <Card withBorder p="md">
      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed" fw={500}>
          {title}
        </Text>
        <ThemeIcon color={color} variant="light" size="lg">
          {icon}
        </ThemeIcon>
      </Group>
      <Text size="xl" fw={700} c={color}>
        {value.toLocaleString()}
      </Text>
      <Text size="xs" c="dimmed">
        {description}
      </Text>
    </Card>
  );
}

export default BlockStats;
